import React, { useState } from "react";
import { Card, Typography, Button } from "antd";
import markdownit from "markdown-it";
import { Agent } from "@/lib/api/resources/agents";

const { Text } = Typography;

interface AgentTileProps {
  agent: Agent;
  selected: boolean;
  onSelect: () => void;
  onDoubleClick?: () => void;
}

export const AgentTile: React.FC<AgentTileProps> = ({
  agent,
  selected,
  onSelect,
  onDoubleClick,
}) => {
  const [expanded, setExpanded] = useState(false);
  const md = markdownit({ html: true, breaks: true });

  const labelString = agent.name || agent.id || "Unnamed Agent";

  // Read the agent's LLM configuration from its read-safe connection (the legacy top-level
  // model/temperature fields are no longer serialized by the API).
  const aiConnection = agent.aiConnection;
  const modelLabel = aiConnection?.model ?? "—";
  const providerLabel = aiConnection?.providerLabel;
  const temperature =
    aiConnection && !Array.isArray(aiConnection.options)
      ? aiConnection.options?.temperature
      : undefined;

  return (
    <Card
      hoverable
      style={{
        borderColor: selected ? "#1890ff" : undefined,
        borderWidth: selected ? 2 : 1,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      styles={{ body: { flex: 1, display: "flex", flexDirection: "column" } }}
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
    >
      <Card.Meta
        title={<span style={{ whiteSpace: "normal" }}>{labelString}</span>}
        description={
          <Text type="secondary">
            {providerLabel ? `${providerLabel} · ` : ""}
            {modelLabel}
          </Text>
        }
      />
      <div
        style={{
          marginTop: 16,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ marginBottom: 8 }}>
          <Text strong>Name:</Text> <Text>{agent.name ?? "N/A"}</Text>
          <br />
          <Text strong>Temperature:</Text> <Text>{temperature ?? "N/A"}</Text>
          <br />
          <Text strong>Max Tokens:</Text>{" "}
          <Text>{agent.maxTokensPerSession ?? "N/A"}</Text>
        </div>

        {agent.instructions && (
          <div style={{ marginTop: "auto" }}>
            <Text strong>Instructions:</Text>
            <div
              style={{
                position: "relative",
                marginTop: 4,
                border: "1px solid #f0f0f0",
                borderRadius: 4,
                padding: 8,
                background: "#fafafa",
              }}
            >
              <div
                style={{
                  maxHeight: expanded ? "none" : "60px",
                  overflow: "hidden",
                  fontSize: "12px",
                }}
                dangerouslySetInnerHTML={{
                  __html: md.render(agent.instructions),
                }}
              />
              {!expanded && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    background:
                      "linear-gradient(to right, transparent, #fafafa 20%)",
                    paddingLeft: 24,
                    paddingRight: 4,
                  }}
                >
                  <Button
                    type="link"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(true);
                    }}
                  >
                    ...
                  </Button>
                </div>
              )}
            </div>
            {expanded && (
              <div style={{ textAlign: "right", marginTop: 4 }}>
                <Button
                  type="link"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded(false);
                  }}
                >
                  Collapse
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
